import { Suspense, useState, type ReactNode } from "react";
import { Link } from "react-router";

import {
  useBreakdown,
  useLookups,
  useSummary,
  type BreakdownRow,
  type Summary,
} from "../../api/hooks.ts";
import { localToday } from "../../domain/breakdown.ts";
import {
  DIMENSION_GROUPS,
  RANGES,
  dimensionInfo,
  rangeStart,
  tableFootText,
  type BreakdownDimension,
  type RangeKey,
} from "../../domain/breakdown.ts";
import { formatPercent, formatR, formatSignedPips, toneOf } from "../../domain/format.ts";
import { Bars, type BarItem } from "../../ui/Bars.tsx";
import { Icon } from "../../ui/Icon.tsx";
import { LoadingPart, lazyPart } from "../../ui/lazyPart.tsx";
import { StatusBlock } from "../../ui/StatusBlock.tsx";
import { usePermissions } from "../auth/permissions.ts";
import { InstrumentPips } from "./InstrumentPips.tsx";
import { PeriodsPanel } from "./PeriodsPanel.tsx";

/* El calendario y el diario del dia que abre pesan lo suyo y no hacen falta
   para leer los desgloses: viajan aparte y se piden al llegar aqui. */
const CalendarPanel = lazyPart(() => import("./CalendarPanel.tsx").then((m) => m.CalendarPanel));

/**
 * Metricas: las mismas operaciones miradas por una de las catorce dimensiones de
 * SPEC 11.
 *
 * Una dimension cada vez, a proposito. Catorce tablas a la vez no es un informe,
 * es un muro: la pregunta se elige y la pantalla la contesta entera.
 *
 * El orden de las filas lo manda el SERVIDOR y aqui no se toca. Viene por la
 * clave de la serie (el orden del catalogo, la hora, el dia, el tramo) y no por
 * el resultado, porque una serie ordinal reordenada por R deja de leerse: "los
 * lunes" ya no estan antes que "los martes" y el ojo pierde la forma.
 */
export function Metrics(): ReactNode {
  /* El atajo para registrar solo se ofrece a quien puede registrar. */
  const { canWriteTrades } = usePermissions();
  const [dimension, setDimension] = useState<BreakdownDimension>("instrument");
  const [range, setRange] = useState<RangeKey>("all");

  const lookups = useLookups();
  const timeZone = lookups.data?.app.timeZone;
  const from = timeZone === undefined ? undefined : rangeStart(range, timeZone);

  /* Hasta que no se sabe la zona horaria del servidor no se puede pedir un
     rango: calcularlo con el reloj del telefono cambiaria el periodo al viajar
     (SPEC 19.19). Con "Todo" no hace falta esperar a nada. */
  const ready = range === "all" || from !== undefined;

  const breakdown = useBreakdown(dimension, from);
  const summary = useSummary(from);

  /* El calendario parte del dia del SERVIDOR, igual que los rangos: con el
     reloj del telefono, un viaje cambiaria el mes que se abre por defecto y el
     dia que lleva el contorno (SPEC 19.19). */
  const today = timeZone === undefined ? null : localToday(timeZone);

  const info = dimensionInfo(dimension);
  const rows = breakdown.data?.rows ?? [];
  const note = breakdown.data?.note ?? null;

  return (
    <>
      <div className="sec-h">
        <div>
          <h2>{info.label}</h2>
          <p>{info.question}</p>
        </div>
        <div className="chips" role="group" aria-label="Periodo">
          {RANGES.map((item) => (
            <button
              key={item.key}
              type="button"
              className={range === item.key ? "chip active" : "chip"}
              aria-pressed={range === item.key}
              onClick={() => setRange(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid" style={{ marginBottom: 16 }}>
        <section className="panel col-7">
          <div className="panel-h">
            <div>
              <h3>Que quieres mirar</h3>
              <p>Las catorce lecturas de SPEC 11, sobre las mismas operaciones cerradas</p>
            </div>
          </div>
          <div className="panel-b dim-groups">
            {DIMENSION_GROUPS.map((group) => (
              <div key={group.title}>
                <span className="dim-group">
                  <Icon name={group.icon} />
                  {group.title}
                </span>
                <div className="chips" role="group" aria-label={group.title}>
                  {group.items.map((item) => (
                    <button
                      key={item.value}
                      type="button"
                      className={dimension === item.value ? "chip active" : "chip"}
                      aria-pressed={dimension === item.value}
                      onClick={() => setDimension(item.value)}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* El calendario es un PANEL de Metricas, no una seccion del menu: es
            justo lo que hace la plantilla, y el mes en mapa de calor contesta
            la misma pregunta que los desgloses desde el otro lado. */}
        {today !== null && (
          <Suspense fallback={<LoadingPart />}>
            <CalendarPanel today={today} />
          </Suspense>
        )}
      </div>

      {summary.data !== undefined && <Ledger data={summary.data} />}

      {/* Los pips POR SEMANA Y POR MES, con el R al lado, y los pips POR
          INSTRUMENTO, que es donde sumarlos mide algo (ADR 0007). Van antes
          del desglose porque son lo que el propietario abre esta pantalla a
          mirar. Esperan al mismo `from` que el resto. */}
      {ready && (
        <div className="grid" style={{ marginBottom: 16 }}>
          <PeriodsPanel from={from} timeZone={timeZone} className="col-8" />
          <InstrumentPips from={from} className="col-4" />
        </div>
      )}

      <StatusBlock
        loading={!ready || breakdown.isPending}
        error={breakdown.error}
        empty={rows.length === 0}
        emptyIcon="chart"
        emptyTitle="Sin operaciones cerradas en este periodo."
        emptyText="Las metricas solo cuentan operaciones cerradas. Amplia el rango o cierra la primera."
        emptyAction={
          canWriteTrades ? (
            <Link className="btn primary" to="/trades/new">
              <Icon name="plus" />
              Registrar una operacion
            </Link>
          ) : undefined
        }
        onRetry={() => void breakdown.refetch()}
      >
        <Results rows={rows} note={note} label={info.label} dimension={dimension} />
      </StatusBlock>
    </>
  );
}

/**
 * El `note` del contrato, PINTADO y arriba del todo.
 *
 * Cuatro dimensiones cuentan la misma operacion en varias filas, y si eso no se
 * dice, el usuario suma la columna, le sale mas que su total y cree que el
 * journal miente. Y desde el ADR 0007 la misma nota avisa tambien de que una
 * suma de pips mezcla instrumentos. Que lo lea no es un adorno: es la
 * diferencia entre una tabla que se entiende y una que se abandona.
 *
 * El titulo es neutro a proposito: el texto lo escribe el servidor y puede
 * traer uno de los dos avisos o los dos.
 */
function Note(props: { text: string }): ReactNode {
  return (
    <div className="infobox" style={{ marginBottom: 16 }}>
      <b>Antes de leer la tabla</b>
      <p>{props.text}</p>
    </div>
  );
}

/**
 * Los seis KPI del periodo. Los pips van PRIMERO y con el R al lado (ADR 0007):
 * pips y R, no pips en vez de R. Y si la suma de pips mezcla instrumentos, el
 * `note` del resumen lo dice y aqui se pinta debajo: un pip de GAIN 400 no es
 * un pip de V75.
 */
function Ledger(props: { data: Summary }): ReactNode {
  const { data } = props;

  return (
    <>
      <div className="ledger">
        <Kpi
          label="Pips"
          value={formatSignedPips(data.totalPips)}
          tone={toneOf(data.totalPips)}
          note={`${data.totalTrades} ${data.totalTrades === 1 ? "operacion" : "operaciones"} en ${data.instruments} ${data.instruments === 1 ? "instrumento" : "instrumentos"}`}
        />
        <Kpi label="R acumulado" value={formatR(data.totalR)} tone={toneOf(data.totalR)} note="En el periodo" />
        <Kpi
          label="Expectativa"
          value={formatR(data.expectancyR)}
          tone={toneOf(data.expectancyR)}
          note="Por operacion con R"
        />
        <Kpi
          label="Acierto"
          value={formatPercent(data.winRate)}
          note={`${data.wins} de ${data.decided} decididas`}
        />
        <Kpi label="Adherencia" value={formatPercent(data.averageAdherence)} note="Reglas del plan cumplidas" />
        <Kpi
          label="Innegociables"
          value={String(data.brokenMandatoryRuleTrades)}
          tone={data.brokenMandatoryRuleTrades > 0 ? "breach" : undefined}
          note="Operaciones que rompieron una"
        />
      </div>

      {data.note !== null && (
        <p className="infobox" style={{ marginBottom: 16 }}>
          <b>Un pip de un instrumento no es un pip de otro.</b> {data.note} El R es la cifra que
          si se puede comparar.
        </p>
      )}
    </>
  );
}

function Kpi(props: {
  label: string;
  value: string;
  note: string;
  tone?: string | undefined;
}): ReactNode {
  return (
    <div className="kpi">
      <small>{props.label}</small>
      <b className={props.tone === undefined ? undefined : props.tone}>{props.value}</b>
      <span className="delta">{props.note}</span>
    </div>
  );
}

function Results(props: {
  rows: readonly BreakdownRow[];
  note: string | null;
  label: string;
  dimension: BreakdownDimension;
}): ReactNode {
  const { rows } = props;

  const rBars: BarItem[] = rows.map((row) => ({
    label: row.label,
    value: row.totalR,
    display: formatR(row.totalR),
    color: row.totalR >= 0 ? "var(--up)" : "var(--down)",
    tone: toneOf(row.totalR),
    title: `${row.label}: ${formatR(row.totalR)} en ${row.trades} operaciones`,
  }));

  /* El acierto va en un color neutro y no en verde o rojo por umbral: un 70 % de
     acierto con expectativa negativa no es una buena lectura, y pintarlo de
     verde diria lo contrario. El juicio lo pone la columna de expectativa. */
  const winBars: BarItem[] = rows
    .filter((row) => row.winRate !== null)
    .map((row) => ({
      label: row.label,
      value: row.winRate ?? 0,
      display: formatPercent(row.winRate),
      color: "var(--primary)",
      title: `${row.label}: ${row.wins} ganadas y ${row.losses} perdidas`,
    }));

  const totalTrades = rows.reduce((sum, row) => sum + row.trades, 0);

  return (
    <>
      {props.note !== null && <Note text={props.note} />}

      <div className="grid">
        <section className="panel col-7">
          <div className="panel-h">
            <div>
              <h3>Resultado en R</h3>
              <p>Lo que suma cada fila, con su signo</p>
            </div>
            <span className="tag">
              <Icon name="trend" />
              {rows.length} {rows.length === 1 ? "fila" : "filas"}
            </span>
          </div>
          <div className="panel-b">
            <Bars items={rBars} />
          </div>
        </section>

        <section className="panel col-5">
          <div className="panel-h">
            <div>
              <h3>Acierto</h3>
              <p>Sobre las decididas, sin las que quedaron en tablas</p>
            </div>
          </div>
          <div className="panel-b">
            <Bars items={winBars} max={1} />
          </div>
        </section>

        <section className="panel col-12">
          <div className="panel-h">
            <div>
              <h3>El detalle</h3>
              <p>Las doce lecturas de cada fila, en el orden de la serie</p>
            </div>
          </div>
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th scope="col">{props.label}</th>
                  <th scope="col">Operaciones</th>
                  <th scope="col">Con R</th>
                  <th scope="col">G / P / T</th>
                  <th scope="col">Acierto</th>
                  <th scope="col">Pips</th>
                  <th scope="col">R total</th>
                  <th scope="col">Expectativa</th>
                  <th scope="col">Mejor</th>
                  <th scope="col">Peor</th>
                  <th scope="col">Adherencia</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.label}>
                    <th scope="row" style={{ textAlign: "left", fontWeight: 600 }}>
                      {row.label}
                    </th>
                    <td>{row.trades}</td>
                    <td>{row.rated}</td>
                    <td>{`${row.wins} / ${row.losses} / ${row.breakEven}`}</td>
                    <td>{formatPercent(row.winRate)}</td>
                    {/* Los pips de la fila, y entre cuantos instrumentos se
                        reparten: con mas de uno, la cifra no se compara sola. */}
                    <td
                      className={toneOf(row.totalPips)}
                      title={`${row.instruments} ${row.instruments === 1 ? "instrumento" : "instrumentos"}`}
                    >
                      {formatSignedPips(row.totalPips)}
                      {row.instruments > 1 && <sup> {row.instruments}</sup>}
                    </td>
                    <td className={toneOf(row.totalR)}>{formatR(row.totalR)}</td>
                    <td className={toneOf(row.expectancyR)}>{formatR(row.expectancyR)}</td>
                    <td>{formatR(row.bestR)}</td>
                    <td>{formatR(row.worstR)}</td>
                    <td>{formatPercent(row.averageAdherence)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="tbl-foot">
            <span>
              {rows.length} {rows.length === 1 ? "fila" : "filas"}, {totalTrades}{" "}
              {totalTrades === 1 ? "operacion sumada" : "operaciones sumadas"}
            </span>
            <span>{tableFootText(props.dimension)}</span>
          </div>
        </section>
      </div>
    </>
  );
}
